<template>
  <div>
    <b-alert v-if="hasReachedMaxArgumentCount" show variant="danger">
      Nezbývají vám už žádné volné argumenty pro logování. Pokud chcete logovat více argumentů, nastavte si vyšší tarif služeb. <b-link :to="{ name: 'subscription' }">Nastavit</b-link>
    </b-alert>
    <b-table
      id="logged-vars-list"
      ref="selectableTable"
      selectable
      select-mode="range"
      selected-variant="success"
      :items="getTableData"
      :fields="fields"
      @row-selected="onRowSelected"
      responsive="sm"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :hover="true"
      :tbody-tr-class="rowClass"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      :filter-function="filterVars"
      @filtered="onFiltered"
    >
      <template v-slot:top-row="data">
        <b-td width="200px">
          <multiselect
            :multiple="true"
            v-model="filterPlace"
            :options="getPlaces"
            :searchable="true"
            :allow-empty="true"
            :close-on-select="true"
            :show-labels="false"
            track-by="id"
            label="name"
            :placeholder="$t('choose')">
            <template
              slot="singleLabel" slot-scope="{ option }">
              <strong>{{ option.name }}</strong>
            </template>
          </multiselect>
        </b-td>
        <b-td colspan="1">
          <multiselect
            :multiple="true"
            v-model="filterGroup"
            :options="getServiceGroups"
            :searchable="false"
            :allow-empty="true"
            :close-on-select="true"
            :show-labels="false"
            track-by="id"
            label="name"
            :placeholder="$t('choose')">
            <template
              slot="singleLabel" slot-scope="{ option }">
              <strong>{{ option.name }}</strong>
            </template>
          </multiselect>
        </b-td>
        <b-td colspan="1">
          <multiselect
            :multiple="true"
            v-model="filterService"
            :options="getServices"
            :searchable="true"
            :allow-empty="true"
            :close-on-select="true"
            :show-labels="false"
            track-by="id"
            label="name"
            :placeholder="$t('choose')">
            <template
              slot="singleLabel" slot-scope="{ option }">
              <strong>{{ option.name }}</strong>
            </template>
          </multiselect>
        </b-td>
        <b-td colspan="1">
          <multiselect
            :multiple="true"
            v-model="filterArgument"
            :options="loggedVarsOptions"
            :searchable="true"
            :allow-empty="true"
            :close-on-select="true"
            :show-labels="false"
            :placeholder="$t('choose')">
            <template
              slot="singleLabel" slot-scope="{ option }">
              <strong>{{ option.name }}</strong>
            </template>
          </multiselect>
        </b-td>
        <b-td>
          <b-form-checkbox
            v-if="isUserInstaller"
            id="checkbox-1"
            v-model="includeSettings"
            name="checkbox-1"
          >
            {{ $t('include-settings') }}
          </b-form-checkbox>
        </b-td>
      </template>
      <template v-slot:cell(loggingEnabled)="{ item }">
        <setter :var-item="item.varItem"/>
      </template>
      <template v-slot:cell(argument)="{ item }">
        {{ getArgumentName(item.varItem) }}
      </template>
      <template v-slot:cell(quickAction)="{ item }">
        <b-button variant="outline-dark" size="sm" :title="$t('copy-argument-link-to-clipboard')" @click="copyArgumentLinkToClipboard(item.varItem.link)">
          <font-awesome-icon icon="copy" class="text-black-25" />
        </b-button>
      </template>
      <template v-slot:cell(lastLoggingEntryAtText)="{ item }">
        <span :id="`last-logging-entry-${item.varItem.id}`">{{ item.lastLoggingEntryAtText }}</span>
        <b-tooltip
          triggers="hover"
          :target="`last-logging-entry-${item.varItem.id}`"
          :title="item.lastLoggingEntryAtTextDetail"
        />
      </template>
    </b-table>
    <b-row>
      <b-col offset="3" cols="6">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="fill"
          size="sm"
          class="my-0"
        />
      </b-col>
      <b-col>
        <b-button variant="primary" @click="toggleAll">
          {{ showAll ? $t('paginate') : $t('all') }}
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import eventBus from '@/components/utils/slideout-panel/eventBus';
import Setter from '@/components/logging/settings/Setter';
import ToasterMixin from "@/mixins/Toaster";

export default {
  name: 'LoggedVarsList',
  components: { Setter },
   mixins: [
    ToasterMixin,
  ],
  data() {
    return {
      fields: [
        {
          key: 'place',
          sortable: true,
          label: this.$_.capitalize(this.$t('place')),
        },
        {
          key: 'group',
          sortable: true,
          label: this.$_.capitalize(this.$t('group')),
        },
        {
          key: 'service',
          sortable: true,
          label: this.$_.capitalize(this.$t('service')),
        },
        {
          key: 'argument',
          sortable: true,
          label: this.$_.capitalize(this.$t('argument')),
        },
        {
          key: 'loggingEnabled',
          sortable: true,
          label: this.$_.capitalize(this.$t('enabled')),
        },
        {
          key: 'lastLoggingEntryAtText',
          sortable: true,
          label: this.$_.capitalize(this.$t('last')),
        },
        {
          key: 'quickAction',
          sortable: false,
          label: '',
        },
      ],
      items: this.getTableData,
      sortBy: 'argument',
      sortDesc: false,
      totalRows: 1,
      perPageDefault: 20,
      perPage: 20,
      currentPage: 1,
      filterGroup: [],
      filterPlace: [],
      filterService: [],
      filterArgument: [],
      includeSettings: false,
      filter: null,
      selected: [],
      showAll: false,
    };
  },
  computed: {
    ...mapState('app', [
      'logging',
    ]),
    ...mapState('snapshot', [
      'currentSnapshot',
    ]),
    ...mapGetters('service-group', [
      'getServiceGroups',
    ]),
    ...mapGetters('user', [
      'isUserInstaller',
    ]),
    ...mapGetters([
      'getLoggedVars',
      'getActorById',
      'getPlaces',
      'getServices',
    ]),
    ...mapGetters('paid-subscription', [
      'hasReachedMaxArgumentCount',
    ]),
    dtFrom() {
      return this.logging.selectedDtRange.from;
    },
    dtTo() {
      return this.logging.selectedDtRange.to;
    },
    getTableData() {
      return this.getLoggedVars.map(v => ({
        service: v.serviceName,
        group: v.serviceGroupName,
        place: v.placeName,
        argument: this.getArgumentName(v),
        lastLoggingEntryAt: new Date(v.last_log_entry_at),
        lastLoggingEntryAtText: moment(v.last_log_entry_at)
          .format('L'),
        lastLoggingEntryAtTextDetail: moment(v.last_log_entry_at)
          .format('LLL'),
        loggingEnabled: v.logging_enabled,
        varItem: v,
        id: v.id,
        quickAction: v,
      }));
    },
    loggedVarsOptions() {
      return this.getLoggedVars.map(
        v => ({ ...v, name: this.getArgumentName(v) }),
      ).reduce(
        (argumentsNames, currentVar) => {
          if (!this.$_.includes(argumentsNames, currentVar.actorsName)) {
            argumentsNames.push(this.getArgumentName(currentVar));
          }
          return argumentsNames;
        }, [],
      );
    },
  },
  methods: {
    toggleAll() {
      this.showAll = !this.showAll;
    },
    onRowSelected(items) {
      this.selected = items;
      eventBus.$emit('itemsSelected', items);
    },
    selectAllRows() {
      this.$refs.selectableTable.selectAllRows();
    },
    clearSelected() {
      this.$refs.selectableTable.clearSelected();
    },
    rowClass(item, type) {
      const classes = ['cursor-pointer'];
      if (!item || type !== 'row') return '';
      if (!this.isloggingEntryWithinRange(item.lastLoggingEntryAt)) {
        classes.push('row-disabled');
      }
      return classes;
    },
    isloggingEntryWithinRange(lastLoggingEntryAt) {
      if (!(this.dtTo && this.dtFrom)) {
        return false;
      }
      return lastLoggingEntryAt <= this.dtTo;
    },
    getArgumentName(varItem) {
      const actor = this.getActorById(varItem.actorId);
      // if (!actor) {
      //   console.log(varItem);
      // }
      if (actor && actor.control_group === 'settings') { // FIXME find out bad actor and fix on backend
        if (this.currentSnapshot.rev === 2) {
          return `${varItem.actorsTitle} [${varItem.title}]`;
        }
        return `${varItem.actorsName} [${varItem.description}]`;
      }
      if (this.currentSnapshot.rev === 2) {
        return varItem.title;
      }
      return varItem.actorsName;
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    filterVars(item, filter) {
      let result = true;
      result = this.filterGroup.length > 0 ? result && this.$_.includes(this.filterGroup.map(i => i.id), item.varItem.groupId) : result;
      result = this.filterPlace.length > 0 ? result && this.$_.includes(this.filterPlace.map(i => i.id), item.varItem.placeId) : result;
      result = this.filterService.length > 0 ? result && this.$_.includes(this.filterService.map(i => i.id), item.varItem.serviceId) : result;
      result = this.filterArgument.length > 0 ? result && this.$_.includes(this.filterArgument, this.getArgumentName(item.varItem)) : result;
      result = !this.includeSettings ? result && item.varItem.control_group !== 'settings' : result;
      return result;
    },
    updateFilter() {
      this.filter = `
        ${this.filterGroup.length > 0 ? this.filterGroup.map(value => value.id).join(',') : null}:
        ${this.filterPlace.length > 0 ? this.filterPlace.map(value => value.id).join(',') : null}:
        ${this.filterService.length > 0 ? this.filterService.map(value => value.id).join(',') : null}:
        ${this.filterArgument.length > 0 ? this.filterArgument.map(value => value.id).join(',') : null}:
        ${this.includeSettings}
      `;
    },
    copyArgumentLinkToClipboard(link) {
      navigator.clipboard.writeText(link);
      this.showToast('', this.$t('copied-to-clipboard'), 'success');
    },
  },
  created() {
    eventBus.$on('clearSelected', this.clearSelected);
  },
  mounted() {
    this.clearSelected();
    this.totalRows = this.getTableData.length;
    eventBus.$emit('itemsSelected', []);
    this.updateFilter();
  },
  destroyed() {
    eventBus.$off('clearSelected');
  },
  watch: {
    dtFrom() {
      this.$root.$emit('bv::refresh::table', 'logged-vars-list');
    },
    dtTo() {
      this.$root.$emit('bv::refresh::table', 'logged-vars-list');
    },
    filterGroup() {
      this.updateFilter();
    },
    filterPlace() {
      this.updateFilter();
    },
    filterService() {
      this.updateFilter();
    },
    includeSettings() {
      this.updateFilter();
    },
    showAll() {
      if (this.showAll) {
        this.perPage = this.totalRows;
      } else {
        this.perPage = this.perPageDefault;
      }
    },
  },
};
</script>

<style scoped>
</style>
